import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const ConfigPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [testing, setTesting] = useState(false);
    const [saving, setSaving] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const userType = localStorage.getItem('tipoUsuario');

    const [config, setConfig] = useState({
        DB_HOST: '',
        DB_USER: '',
        DB_PASS: '',
        DB_NAME: '',
        DB_PORT: '3306'
    });

    const [originalConfig, setOriginalConfig] = useState({});

    // Corte por sede
    const [sedes, setSedes] = useState([]);
    const [cutoffForm, setCutoffForm] = useState({ id_sede: '', day: '3', time: '10:00' });
    const [editCutoffForm, setEditCutoffForm] = useState({ id_sede: '', day: '3', time: '10:00' });
    const [cutoffConfigs, setCutoffConfigs] = useState({});
    const dias = [
        { value: 0, label: 'Domingo' },
        { value: 1, label: 'Lunes' },
        { value: 2, label: 'Martes' },
        { value: 3, label: 'Miércoles' },
        { value: 4, label: 'Jueves' },
        { value: 5, label: 'Viernes' },
        { value: 6, label: 'Sábado' },
    ];

    useEffect(() => {
        // Verificar que el usuario sea superadmin o admin
        const userType = localStorage.getItem('tipoUsuario');
        if (userType !== 'superadmin' && userType !== 'admin') {
            alert('Acceso denegado. Se requieren permisos de administrador.');
            navigate('/');
            return;
        }

        loadConfig();
        loadSedes();
        loadCutoffs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate]);

    const handleUnauthorized = (status, message) => {
        if (status === 401) {
            alert(message || 'Sesión expirada. Vuelve a iniciar sesión.');
            navigate('/');
            return true;
        }
        if (status === 403) {
            alert(message || 'Acceso denegado. Se requieren permisos de administrador.');
            navigate('/');
            return true;
        }
        return false;
    };

    const loadConfig = async () => {
        try {
            const token = sessionStorage.getItem('token');

            // Si no hay token, redirigir a login
            if (!token) {
                alert('Sesión expirada. Vuelve a iniciar sesión.');
                navigate('/');
                return;
            }

            const response = await fetch(`${process.env.REACT_APP_API_URL}/config`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.ok) {
                const configValues = await response.json();
                setConfig(configValues);
                setOriginalConfig(configValues);
            } else {
                console.error('Error al cargar configuración');
            }
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        setConfig({
            ...config,
            [e.target.name]: e.target.value
        });
        setTestResult(null); // Limpiar resultado del test al cambiar
    };

    const handleTestConnection = async () => {
        setTesting(true);
        setTestResult(null);

        try {
            const token = sessionStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/config/test`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(config)
            });

            const result = await response.json();

            if (response.ok) {
                setTestResult({
                    success: true,
                    message: result.message || 'Conexión exitosa',
                    details: result.details
                });
            } else {
                setTestResult({
                    success: false,
                    message: result.message || 'Error en la conexión',
                    error: result.error
                });
            }
        } catch (error) {
            setTestResult({
                success: false,
                message: 'Error al probar conexión',
                error: error.message
            });
        } finally {
            setTesting(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);

        try {
            const token = sessionStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/config`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(config)
            });

            if (handleUnauthorized(response.status)) {
                return;
            }

            if (response.ok) {
                alert('Configuración guardada exitosamente');
                setOriginalConfig(config);
                setTestResult(null);
            } else {
                const error = await response.json();
                alert('Error al guardar configuración: ' + (error.message || response.statusText));
            }
        } catch (error) {
            alert('Error al guardar configuración: ' + error.message);
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setConfig(originalConfig);
        setTestResult(null);
    };

    const hasChanges = JSON.stringify(config) !== JSON.stringify(originalConfig);

    async function loadSedes() {
        try {
            const token = sessionStorage.getItem('token');
            const resp = await fetch(`${process.env.REACT_APP_API_URL}/addres`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (handleUnauthorized(resp.status)) {
                return;
            }
            if (resp.ok) {
                const data = await resp.json();
                setSedes(Array.isArray(data) ? data : []);
            }
        } catch (e) {
            // noop
        }
    }

    // Carga configuraciones de corte existentes
    async function loadCutoffs() {
        try {
            const token = sessionStorage.getItem('token');
            const resp = await fetch(`${process.env.REACT_APP_API_URL}/config/cutoff`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (handleUnauthorized(resp.status)) {
                return;
            }
            if (resp.ok) {
                const data = await resp.json();
                setCutoffConfigs(data || {});
                // si hay default, precargar al form
                const def = data?.CUTOFF_CREATE_DEFAULT || data?.CUTOFF_DEFAULT;
                if (def && typeof def.day === 'number' && typeof def.time === 'string') {
                    setCutoffForm((f) => ({ ...f, day: String(def.day), time: def.time }));
                }
                const defEdit = data?.CUTOFF_EDIT_DEFAULT;
                if (defEdit && typeof defEdit.day === 'number' && typeof defEdit.time === 'string') {
                    setEditCutoffForm((f) => ({ ...f, day: String(defEdit.day), time: defEdit.time }));
                }
            }
        } catch (e) {
            // noop
        }
    }

    function handleCutoffChange(e) {
        const { name, value } = e.target;
        setCutoffForm((f) => ({
            ...f,
            [name]: value
        }));
    }

    async function saveCutoff() {
        const token = sessionStorage.getItem('token');
        const payload = {
            id_sede: cutoffForm.id_sede === '' ? null : parseInt(cutoffForm.id_sede, 10),
            day: parseInt(cutoffForm.day, 10),
            time: cutoffForm.time,
        };
        if (!/^\d{2}:\d{2}$/.test(payload.time)) {
            alert('La hora debe tener formato HH:MM');
            return;
        }
        const res = await fetch(`${process.env.REACT_APP_API_URL}/config/cutoff`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ ...payload, scope: 'create' })
        });
        if (handleUnauthorized(res.status)) {
            return;
        }
        if (res.ok) {
            alert('Horario de corte guardado');
            await loadCutoffs();
        } else {
            const err = await res.json().catch(() => ({}));
            alert('No se pudo guardar el horario de corte: ' + (err.message || res.status));
        }
    }

    function handleEditCutoffChange(e) {
        const { name, value } = e.target;
        setEditCutoffForm((f) => ({
            ...f,
            [name]: value
        }));
    }

    async function saveEditCutoff() {
        const token = sessionStorage.getItem('token');
        const payload = {
            id_sede: editCutoffForm.id_sede === '' ? null : parseInt(editCutoffForm.id_sede, 10),
            day: parseInt(editCutoffForm.day, 10),
            time: editCutoffForm.time,
        };
        if (!/^\d{2}:\d{2}$/.test(payload.time)) {
            alert('La hora debe tener formato HH:MM');
            return;
        }
        const res = await fetch(`${process.env.REACT_APP_API_URL}/config/cutoff`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ ...payload, scope: 'edit' })
        });
        if (handleUnauthorized(res.status)) {
            return;
        }
        if (res.ok) {
            alert('Horario de corte de edición guardado');
            await loadCutoffs();
        } else {
            const err = await res.json().catch(() => ({}));
            alert('No se pudo guardar el horario de edición: ' + (err.message || res.status));
        }
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-xl text-gray-600">Cargando configuración...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4">
            <div className="max-w-3xl mx-auto">
                {/* Solo mostrar config de BD si es superadmin y hay datos cargados */}
                {userType === 'superadmin' && Object.keys(originalConfig).length > 0 && (
                    <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                        <div className="mb-6">
                            <h1 className="text-3xl font-bold text-gray-800">Configuración de Base de Datos</h1>
                            <p className="text-gray-600 mt-2">
                                Configure la conexión a la base de datos. Los cambios se aplicarán en caliente sin necesidad de reiniciar.
                            </p>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Host del Servidor
                                </label>
                                <input
                                    type="text"
                                    name="DB_HOST"
                                    value={config.DB_HOST}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Ej: 34.176.164.98"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Puerto
                                </label>
                                <input
                                    type="text"
                                    name="DB_PORT"
                                    value={config.DB_PORT}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="3306"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Usuario
                                </label>
                                <input
                                    type="text"
                                    name="DB_USER"
                                    value={config.DB_USER}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="root"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Contraseña
                                </label>
                                <input
                                    type="password"
                                    name="DB_PASS"
                                    value={config.DB_PASS === '********' ? '' : config.DB_PASS}
                                    onChange={handleChange}
                                    placeholder={config.DB_PASS === '********' ? 'Ingresa nueva contraseña para cambiar' : ''}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                {config.DB_PASS === '********' && (
                                    <p className="text-xs text-gray-500 mt-1">
                                        Por seguridad, la contraseña no se muestra. Déjala vacía si no quieres cambiarla.
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Nombre de la Base de Datos
                                </label>
                                <input
                                    type="text"
                                    name="DB_NAME"
                                    value={config.DB_NAME}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="semanalmenu_prueba"
                                />
                            </div>
                        </div>

                        {testResult && (
                            <div className={`mt-4 p-4 rounded-md ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                                <div className={`font-semibold ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
                                    {testResult.success ? '✅ ' : '❌ '}
                                    {testResult.message}
                                </div>
                                {testResult.details && (
                                    <div className="text-sm text-gray-600 mt-2">
                                        <div>Host: {testResult.details.host}</div>
                                        <div>Base de datos: {testResult.details.database}</div>
                                        <div>Puerto: {testResult.details.port}</div>
                                    </div>
                                )}
                                {testResult.error && (
                                    <div className="text-sm text-red-600 mt-2">
                                        Error: {testResult.error}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="mt-6 flex gap-3">
                            <button
                                onClick={handleTestConnection}
                                disabled={testing}
                                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
                            >
                                {testing ? 'Probando...' : '🔌 Probar Conexión'}
                            </button>

                            <button
                                onClick={handleSave}
                                disabled={saving || !hasChanges}
                                className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
                            >
                                {saving ? 'Guardando...' : '💾 Guardar Cambios'}
                            </button>

                            {hasChanges && (
                                <button
                                    onClick={handleCancel}
                                    className="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 font-medium"
                                >
                                    Cancelar
                                </button>
                            )}

                            <button
                                onClick={() => navigate(-1)}
                                className="ml-auto px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 font-medium"
                            >
                                Volver
                            </button>
                        </div>
                    </div>
                )}

                {/* Configuración de cortes por sede - Creación */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                    <div className="mb-6">
                        <h2 className="text-2xl font-bold text-gray-800">Corte para crear pedidos</h2>
                        <p className="text-gray-600 mt-2">
                            Define el día y la hora en la que deja de aceptarse la carga de pedidos (crear). Puedes establecer un valor por sede o un valor por defecto para todas.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Sede</label>
                            <select
                                name="id_sede"
                                value={cutoffForm.id_sede}
                                onChange={handleCutoffChange}
                                className="w-full min-w-[180px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base bg-white text-gray-900"
                            >
                                <option value="">(Default para todas)</option>
                                {sedes.map((s) => (
                                    <option key={s.id_direccion} value={String(s.id_direccion)}>
                                        {s.nombre_sede || `Sede ${s.id_direccion}`}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Día</label>
                            <select
                                name="day"
                                value={cutoffForm.day}
                                onChange={handleCutoffChange}
                                className="w-full min-w-[140px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base bg-white text-gray-900"
                            >
                                {dias.map(d => (
                                    <option key={d.value} value={String(d.value)}>{d.label}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Hora</label>
                            <input
                                type="time"
                                name="time"
                                value={cutoffForm.time || '10:00'}
                                onChange={handleCutoffChange}
                                className="w-full min-w-[180px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                            />
                        </div>
                    </div>

                    <div className="mt-4">
                        <button
                            onClick={saveCutoff}
                            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
                        >
                            Guardar horario de corte
                        </button>
                    </div>

                    {/* Vista rápida de configuraciones guardadas */}
                    <div className="mt-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">Configuraciones guardadas</h3>
                        <div className="text-sm text-gray-700 space-y-1">
                            {Object.keys(cutoffConfigs).length === 0 && (
                                <div className="text-gray-500">No hay configuraciones guardadas (se usa Miércoles 10:00 por defecto).</div>
                            )}
                            {Object.entries(cutoffConfigs).map(([k, v]) => (
                                <div key={k} className="flex items-center justify-between border-b py-1">
                                    <div className="font-mono text-gray-600">{k}</div>
                                    <div>{typeof v === 'object' ? `${dias[v.day]?.label || v.day} ${v.time}` : String(v)}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Configuración de cortes por sede - Edición */}
                <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="mb-6">
                        <h2 className="text-2xl font-bold text-gray-800">Corte para editar pedidos</h2>
                        <p className="text-gray-600 mt-2">
                            Define el día y la hora en la que deja de aceptarse la edición de pedidos. Puedes establecer un valor por sede o un valor por defecto para todas.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Sede</label>
                            <select
                                name="id_sede"
                                value={editCutoffForm.id_sede}
                                onChange={handleEditCutoffChange}
                                className="w-full min-w-[140px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base bg-white text-gray-900"
                            >
                                <option value="">(Default para todas)</option>
                                {sedes.map((s) => (
                                    <option key={s.id_direccion} value={String(s.id_direccion)}>
                                        {s.nombre_sede || `Sede ${s.id_direccion}`}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Día</label>
                            <select
                                name="day"
                                value={editCutoffForm.day}
                                onChange={handleEditCutoffChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
                            >
                                {dias.map(d => (
                                    <option key={d.value} value={String(d.value)}>{d.label}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Hora</label>
                            <input
                                type="time"
                                name="time"
                                value={editCutoffForm.time || '10:00'}
                                onChange={handleEditCutoffChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>

                    <div className="mt-4">
                        <button
                            onClick={saveEditCutoff}
                            className="px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 font-medium"
                        >
                            Guardar horario de edición
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConfigPage;
